/**
 * Role-Based Route Protection
 * LAYER-IDENTITY-ACCESS
 *
 * Protects routes based on user role. Uses AuthContext for centralized
 * authentication state. Defers to ROLE_HIERARCHY in @shared/context for
 * inheritance semantics (ADMIN inherits CM and COMPLIANCE; SUPER_ADMIN
 * inherits ADMIN).
 *
 * Strict portal separation (no STAFF tier shortcut):
 * - CUSTOMER → customer surfaces only.
 * - CASE_MANAGER → CM dashboard. PLUS All Transactions (shared lookup).
 * - COMPLIANCE → Compliance dashboard. PLUS All Transactions.
 * - ADMIN → admin tools + CM dashboard + Compliance dashboard +
 *   All Transactions (by hierarchy).
 * - SUPER_ADMIN → everything.
 *
 * The previous "STAFF tier" model granted CM access to Compliance
 * dashboard and vice versa — removed because the role-management UI
 * needs to be predictable. Granting CM should grant CM dashboard, not
 * silently grant Compliance dashboard too.
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, Role } from '@shared/context';

export type UserRole = 'CUSTOMER' | 'CASE_MANAGER' | 'COMPLIANCE' | 'OPERATIONS' | 'ADMIN' | 'SUPER_ADMIN';

/**
 * Access Tiers — which roles can access which broad areas.
 *
 * STAFF here means "any non-customer role" for surfaces like All
 * Transactions that every staff member needs. It does NOT mean
 * "CM can access Compliance dashboard."
 *
 * OPS_OR_ADMIN: shared "do-the-work" tier — operations staff doing
 * per-customer interventions (refunds, unlocks) plus admins. Used for
 * the read-only audit views (notification templates/rules/sends, reports)
 * and the refund button. Excludes pure CM and pure COMPLIANCE.
 */
export const ACCESS_TIERS = {
  CUSTOMER: ['CUSTOMER'] as UserRole[],
  STAFF: ['CASE_MANAGER', 'COMPLIANCE', 'OPERATIONS', 'ADMIN', 'SUPER_ADMIN'] as UserRole[],
  OPS_OR_ADMIN: ['OPERATIONS', 'ADMIN', 'SUPER_ADMIN'] as UserRole[],
  ADMIN: ['ADMIN', 'SUPER_ADMIN'] as UserRole[],
  SUPER_ADMIN: ['SUPER_ADMIN'] as UserRole[],
} as const;

export type AccessTier = keyof typeof ACCESS_TIERS;

interface RoleRouteProps {
  /** Roles allowed to access this route */
  allowedRoles?: UserRole[];
  /** Access tier for this route (alternative to allowedRoles) */
  accessTier?: AccessTier;
  /** The component to render if authorized */
  children: React.ReactNode;
  /** Path to redirect to if unauthorized (default: /unauthorized) */
  redirectTo?: string;
}

/**
 * Route guard component that checks user role before rendering
 * Now uses AuthContext for centralized auth state.
 *
 * @example Using allowedRoles:
 * <Route
 *   path="/case-manager/*"
 *   element={
 *     <RoleRoute allowedRoles={['CASE_MANAGER', 'ADMIN']}>
 *       <CaseManagerRoutes />
 *     </RoleRoute>
 *   }
 * />
 *
 * @example Using accessTier:
 * <Route
 *   path="/staff/*"
 *   element={
 *     <RoleRoute accessTier="STAFF">
 *       <StaffPortal />
 *     </RoleRoute>
 *   }
 * />
 */
export function RoleRoute({
  allowedRoles,
  accessTier,
  children,
  redirectTo = '/unauthorized',
}: RoleRouteProps) {
  const location = useLocation();
  const { isAuthenticated, isLoading, user, hasRole } = useAuth();

  // Show loading state while auth is being determined
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="spinner" aria-label="Loading..." />
      </div>
    );
  }

  // Check authentication first
  if (!isAuthenticated || !user) {
    // Save intended path for redirect after login
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }

  // Determine allowed roles from either prop
  const rolesAllowed: UserRole[] = allowedRoles || (accessTier ? ACCESS_TIERS[accessTier] : []);

  // If no roles specified, deny access
  if (rolesAllowed.length === 0) {
    console.warn('RoleRoute: No allowedRoles or accessTier specified');
    return <Navigate to={redirectTo} replace />;
  }

  // Check if user has any of the allowed roles (using role hierarchy from AuthContext)
  const hasAccess = rolesAllowed.some((role) => hasRole(role as Role));

  // Debug logging - remove after fixing
  console.log('[RoleRoute] Check:', {
    path: location.pathname,
    userRoles: user?.roles,
    rolesAllowed,
    hasAccess,
  });

  if (!hasAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

/**
 * Get the default route for a given role
 */
export function getDefaultRouteForRole(role: UserRole): string {
  switch (role) {
    case 'CUSTOMER':
      return '/accounts/me';
    case 'CASE_MANAGER':
      return '/case-manager/dashboard';
    case 'COMPLIANCE':
      return '/compliance/queue';
    case 'OPERATIONS':
      // OPS-only users land on All Transactions — their primary surface
      // for finding a customer to refund/unlock. A user with both OPS and
      // (say) CASE_MANAGER will land on the CM dashboard instead because
      // getHighestRole() in roles.ts ranks CM above OPS.
      return '/admin/transactions';
    case 'ADMIN':
    case 'SUPER_ADMIN':
      return '/admin/dashboard';
    default:
      return '/accounts/me';
  }
}

/**
 * Get accessible portals for a role.
 *
 * Strict role-to-portal separation. Use this for "what dashboards does
 * this role unlock?" questions only. For shared lookup surfaces like
 * All Transactions that every staff role needs, gate on the STAFF
 * access tier (or the literal staff role list) instead — those aren't
 * portals, they're tools.
 *
 * Mirrors the backend's getRolePortalAccess in @shared/context — the
 * portal and backend agree on what each role can see.
 */
export function getAccessiblePortals(role: UserRole): string[] {
  switch (role) {
    case 'CUSTOMER':
      return ['customer'];
    case 'CASE_MANAGER':
      return ['case-manager'];
    case 'COMPLIANCE':
      return ['compliance'];
    case 'OPERATIONS':
      return ['operations'];
    case 'ADMIN':
      return ['admin', 'case-manager', 'compliance', 'operations'];
    case 'SUPER_ADMIN':
      return ['admin', 'case-manager', 'compliance', 'operations', 'customer'];
    default:
      return [];
  }
}

export default RoleRoute;
